import React, { useState, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Campaign } from '@/hooks/useCampaigns'
import { Badge } from '@/components/ui/badge'
import { X, Plus, ChevronsUpDown } from 'lucide-react'
import type { AngleOfferEntry } from '@/lib/campaign-angles-offers'

// Phase D: code-generation helpers (mirror the Create wizard).
type DimPrefix = 'A' | 'O' | 'R' | 'S'
function nextAOCode(prefix: DimPrefix, existing: AngleOfferEntry[]): string {
  let n = existing.length + 1
  while (existing.some((e) => e.code === `${prefix}${n}`)) n += 1
  return `${prefix}${n}`
}
function resizeAOEntries(
  current: AngleOfferEntry[],
  target: number,
  prefix: DimPrefix,
): AngleOfferEntry[] {
  if (target <= 0) return []
  if (target === current.length) return current
  if (target < current.length) return current.slice(0, target)
  const next = [...current]
  while (next.length < target) {
    next.push({ code: nextAOCode(prefix, next), name: '', description: '' })
  }
  return next
}

// Normalise a campaign name: trim and collapse any internal whitespace runs
// to a single space. Mirrors CreateCampaignDialog so both paths store names
// identically and version chains don't fork over "  " vs " ".
function normaliseCampaignName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ')
}

interface EditCampaignDialogProps {
  campaign: Campaign | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdate: (campaignId: string, updates: Partial<Campaign>) => void
  // Pass-through campaigns list from the parent (which already calls
  // useCampaigns). Avoids a second fetch + race condition. Optional for
  // back-compat: when omitted the combobox + Other Versions panel are empty.
  allCampaigns?: Campaign[]
}

// Map a campaign_type stored in the DB to the form's representation. Kept
// outside the component so it can be reused by the lazy initialiser below.
//
// DB tolerance: existing data has case + plural drift ("Bulk" capitalised,
// "events" plural where the form expects "event"). We lowercase the input
// and alias both singular + plural so the dropdown renders correctly for
// every Active row currently in production. The symmetric
// mapCampaignTypeToDb writes back canonical values matching
// CreateCampaignDialog's SelectItem values ("triggers"/"events" plural).
function mapCampaignTypeToForm(dbType: string | undefined | null): string {
  const norm = (dbType || '').trim().toLowerCase()
  switch (norm) {
    case 'triggers':
    case 'trigger':
      return 'trigger'
    case 'bulk':
      return 'bulk'
    case 'adhoc':
    case 'ad-hoc':
      return 'adhoc'
    case 'events':
    case 'event':
      return 'event'
    case 'lookalike':
    case 'lookalikes':
      return 'lookalike'
    default:
      return norm
  }
}

function initFormDataFromCampaign(campaign: Campaign | null) {
  if (!campaign) {
    return {
      name: '',
      description: '',
      status: 'active',
      campaign_type: '',
      trigger_type: '',
      version: '1.0',
      clay_table_link: '',
      clickup_task_link: '',
      copywriting_doc_link: '',
    }
  }
  return {
    name: campaign.name || '',
    description: campaign.description || '',
    status: campaign.status || 'active',
    campaign_type: mapCampaignTypeToForm(campaign.campaign_type),
    trigger_type: campaign.trigger_type || '',
    version: campaign.version || '1.0',
    clay_table_link: campaign.clay_table_link || '',
    clickup_task_link: campaign.clickup_task_link || '',
    copywriting_doc_link: campaign.copywriting_doc_link || '',
  }
}

export function EditCampaignDialog({ campaign, open, onOpenChange, onUpdate, allCampaigns = [] }: EditCampaignDialogProps) {
  // LAZY INITIALISERS — run exactly once per mount. Combined with the
  // parent's `key={\`edit-${campaign.id}\`}` remount strategy, this
  // guarantees the form is correctly seeded from the campaign prop without
  // any useEffect race. Earlier we used a useEffect to copy props → state,
  // which mysteriously left formData empty while channels + targetRegions
  // were populated — most likely because Radix Dialog's focus management
  // remounts inputs and clobbered the first state batch. Lazy init is
  // simpler AND correct.
  const [formData, setFormData] = useState(() => initFormDataFromCampaign(campaign))
  const [channels, setChannels] = useState<string[]>(() => campaign?.channels || [])
  const [newChannel, setNewChannel] = useState('')
  const [nameComboOpen, setNameComboOpen] = useState(false)

  // Phase D + H: campaign-level JSONB dimensions. Angles/Offers/Subject
  // Lines are used as fallback when per-segment JSONB is empty (hybrid);
  // for 0-segment campaigns they're the only source. Regions are
  // campaign-only (Q4).
  const [campaignAngles, setCampaignAngles] = useState<AngleOfferEntry[]>(
    () => (Array.isArray(campaign?.angles) ? (campaign!.angles as AngleOfferEntry[]) : [])
  )
  const [campaignOffers, setCampaignOffers] = useState<AngleOfferEntry[]>(
    () => (Array.isArray(campaign?.offers) ? (campaign!.offers as AngleOfferEntry[]) : [])
  )
  const [campaignRegions, setCampaignRegions] = useState<AngleOfferEntry[]>(
    () => (Array.isArray((campaign as any)?.regions) ? ((campaign as any).regions as AngleOfferEntry[]) : [])
  )
  const [campaignSubjectLines, setCampaignSubjectLines] = useState<AngleOfferEntry[]>(
    () => (Array.isArray((campaign as any)?.subject_lines) ? ((campaign as any).subject_lines as AngleOfferEntry[]) : [])
  )

  // `allCampaigns` is now a prop (was previously a second useCampaigns()
  // call inside this component, which caused a double fetch and a race
  // condition that clobbered setFormData on dialog open — visible bug:
  // Name field rendered empty even though the campaign prop had data).

  // Distinct existing names for the SAME client as the campaign being edited.
  // Sorted A-Z for a predictable dropdown.
  const existingNamesForClient = useMemo(() => {
    if (!campaign?.client_id) return [] as string[]
    const seen = new Set<string>()
    const out: string[] = []
    for (const c of allCampaigns) {
      if (c.client_id !== campaign.client_id) continue
      const n = (c.name || '').trim()
      if (!n) continue
      const key = n.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push(n)
    }
    return out.sort((a, b) => a.localeCompare(b))
  }, [allCampaigns, campaign?.client_id])

  // Other campaigns for the same (client_id, name) — these are the version
  // siblings. Case-insensitive + trimmed compare. Excludes the currently
  // edited campaign. Sorted by version desc (string sort — works for "1.0",
  // "2.0" and acceptable for "1.1" etc. Good enough for display).
  const otherVersions = useMemo(() => {
    if (!campaign?.client_id) return [] as Campaign[]
    const targetName = normaliseCampaignName(formData.name).toLowerCase()
    if (!targetName) return []
    return allCampaigns
      .filter((c) =>
        c.client_id === campaign.client_id &&
        c.id !== campaign.id &&
        normaliseCampaignName(c.name || '').toLowerCase() === targetName
      )
      .sort((a, b) => (b.version || '').localeCompare(a.version || ''))
  }, [allCampaigns, campaign?.client_id, campaign?.id, formData.name])

  // No prop→state copy useEffect needed: lazy useState initialisers above
  // seed everything from `campaign` on mount, and the parent remounts this
  // component (via `key={\`edit-${campaign.id}\`}`) whenever a different
  // campaign is selected.

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!campaign) return

    // Normalise before save so "Cruva  - Bulk" stores as "Cruva - Bulk" and
    // keeps the version chain intact.
    const normalisedName = normaliseCampaignName(formData.name)
    if (!normalisedName) {
      // HTML `required` should have caught this, but belt-and-braces for
      // whitespace-only input.
      return
    }

    // Map campaign_type from form format back to database format. Canonical
    // DB values match CreateCampaignDialog's SelectItem values (plural for
    // "triggers"/"events"). Previous version wrote `event` (singular) which
    // would have silently flipped the 5 existing `events` rows on save.
    const mapCampaignTypeToDb = (formType: string) => {
      switch (formType) {
        case 'trigger': return 'triggers'
        case 'bulk': return 'bulk'
        case 'adhoc': return 'adhoc'
        case 'event': return 'events'
        case 'lookalike': return 'lookalike'
        default: return formType || ''
      }
    }

    // Phase D + H: drop empty-name entries before save (user added a slot
    // but didn't fill it). Trim whitespace from names + descriptions.
    const cleanList = (list: AngleOfferEntry[]) =>
      list
        .filter((x) => x.name.trim())
        .map((x) => ({
          code: x.code,
          name: x.name.trim(),
          description: x.description?.trim() || undefined,
        }))
    const cleanedAngles = cleanList(campaignAngles)
    const cleanedOffers = cleanList(campaignOffers)
    const cleanedRegions = cleanList(campaignRegions)
    const cleanedSubjectLines = cleanList(campaignSubjectLines)

    const updates = {
      ...formData,
      name: normalisedName,
      campaign_type: mapCampaignTypeToDb(formData.campaign_type),
      channels,
      // target_regions (legacy TEXT[]) intentionally NOT written here
      // anymore (Phase I Q7 2026-04-22). The column is deprecated; we
      // preserve whatever historical value exists on the row.
      // Phase D + H: all four campaign-level JSONB dimensions.
      angles: cleanedAngles,
      offers: cleanedOffers,
      regions: cleanedRegions,
      subject_lines: cleanedSubjectLines,
    }
    onUpdate(campaign.id, updates)
    onOpenChange(false)
  }

  const addChannel = () => {
    if (newChannel.trim() && !channels.includes(newChannel.trim())) {
      setChannels([...channels, newChannel.trim()])
      setNewChannel('')
    }
  }

  const removeChannel = (channelToRemove: string) => {
    setChannels(channels.filter(channel => channel !== channelToRemove))
  }

  const handleKeyPress = (e: React.KeyboardEvent, addFunction: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addFunction()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Campaign Details</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {/* Phase D: campaign-level angles + offers — moved to the TOP
                (per Sabeeh's feedback on 2026-04-22) for consistency with
                the Create wizard. Used by 0-segment campaigns (Duel V1,
                Cruva V2, StoryStream V3-after-relabel). For campaigns WITH
                segments, the per-segment lists win and are edited via the
                Segment Detail UI (future phase). */}
            <div className="space-y-3 rounded-md border bg-muted/20 p-3">
              <Label className="text-sm font-medium">
                Campaign-level Dimensions
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  (angles/offers/subject-lines are fallbacks when segments
                  don't define their own; regions are always campaign-only)
                </span>
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Angles
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="20"
                    value={campaignAngles.length}
                    onChange={(e) => {
                      const n = Math.max(0, parseInt(e.target.value, 10) || 0)
                      setCampaignAngles((prev) => resizeAOEntries(prev, n, 'A'))
                    }}
                    className="w-32"
                  />
                  {campaignAngles.map((a, i) => (
                    <div key={a.code} className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground w-10">{a.code}</span>
                      <Input
                        value={a.name}
                        onChange={(e) =>
                          setCampaignAngles((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))
                          )
                        }
                        placeholder={`Angle ${i + 1} name`}
                      />
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Offers
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="20"
                    value={campaignOffers.length}
                    onChange={(e) => {
                      const n = Math.max(0, parseInt(e.target.value, 10) || 0)
                      setCampaignOffers((prev) => resizeAOEntries(prev, n, 'O'))
                    }}
                    className="w-32"
                  />
                  {campaignOffers.map((o, i) => (
                    <div key={o.code} className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground w-10">{o.code}</span>
                      <Input
                        value={o.name}
                        onChange={(e) =>
                          setCampaignOffers((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))
                          )
                        }
                        placeholder={`Offer ${i + 1} name`}
                      />
                    </div>
                  ))}
                </div>
                {/* Phase H: Regions (campaign-only). */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Regions
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="20"
                    value={campaignRegions.length}
                    onChange={(e) => {
                      const n = Math.max(0, parseInt(e.target.value, 10) || 0)
                      setCampaignRegions((prev) => resizeAOEntries(prev, n, 'R'))
                    }}
                    className="w-32"
                  />
                  {campaignRegions.map((r, i) => (
                    <div key={r.code} className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground w-10">{r.code}</span>
                      <Input
                        value={r.name}
                        onChange={(e) =>
                          setCampaignRegions((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))
                          )
                        }
                        placeholder={`Region ${i + 1} (e.g. EU, USA)`}
                      />
                    </div>
                  ))}
                </div>
                {/* Phase H: Subject Lines (hybrid). */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Subject Lines
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="20"
                    value={campaignSubjectLines.length}
                    onChange={(e) => {
                      const n = Math.max(0, parseInt(e.target.value, 10) || 0)
                      setCampaignSubjectLines((prev) => resizeAOEntries(prev, n, 'S'))
                    }}
                    className="w-32"
                  />
                  {campaignSubjectLines.map((s, i) => (
                    <div key={s.code} className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground w-10">{s.code}</span>
                      <Input
                        value={s.name}
                        onChange={(e) =>
                          setCampaignSubjectLines((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))
                          )
                        }
                        placeholder={`Subject line ${i + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Edits update the campaign's JSONB columns. Existing variants
                keep their code references — codes stay stable, only names
                change. To create new variants for newly-added entries, use
                Create Campaign or add manually from the A/B Test tab.
              </p>
            </div>

            {/* Other versions of this campaign — read-only panel, rendered
                ABOVE the Name input so the auto-opening Name combobox doesn't
                cover it. Updates live as the user types/picks a name. */}
            {otherVersions.length > 0 && (
              <div className="space-y-2 rounded-md border bg-muted/20 p-3">
                <Label className="text-sm font-medium">
                  Other versions of "{normaliseCampaignName(formData.name)}"
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    ({otherVersions.length} other{otherVersions.length === 1 ? '' : 's'})
                  </span>
                </Label>
                <ul className="space-y-1 text-sm">
                  {otherVersions.map((c) => (
                    <li key={c.id} className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono">
                        v{c.version || '?'}
                      </Badge>
                      <span className="text-muted-foreground capitalize">{c.status}</span>
                      {c.campaign_type && (
                        <span className="text-xs text-muted-foreground">
                          · {c.campaign_type}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Saving this campaign with the same name will keep it grouped
                  with these siblings.
                </p>
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Campaign Name *</Label>
                {/* Combobox: pick an existing name for this client (version
                    sibling) or keep typing a new one. Client is implicit (the
                    campaign already has a client_id), so no gating needed. */}
                <Popover open={nameComboOpen} onOpenChange={setNameComboOpen}>
                  <div className="flex gap-2">
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      onFocus={() => {
                        if (existingNamesForClient.length > 0) setNameComboOpen(true)
                      }}
                      placeholder="Pick an existing campaign name or type a new one"
                      required
                      aria-required="true"
                      className="flex-1"
                    />
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={existingNamesForClient.length === 0}
                        aria-label="Show existing campaign names for this client"
                      >
                        <ChevronsUpDown className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                  </div>
                  <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="end">
                    <Command
                      filter={(value, search) =>
                        value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
                      }
                    >
                      <CommandInput placeholder="Search existing names..." />
                      <CommandList>
                        <CommandEmpty>
                          No matching campaign for this client. Type a name above to create a new one.
                        </CommandEmpty>
                        <CommandGroup heading="Existing campaigns for this client">
                          {existingNamesForClient.map((n) => (
                            <CommandItem
                              key={n}
                              value={n}
                              onSelect={() => {
                                setFormData((prev) => ({ ...prev, name: n }))
                                setNameComboOpen(false)
                              }}
                            >
                              {n}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  To save as a new version of an existing campaign, reuse the
                  exact same name. Pick from the list when possible.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter campaign description"
                rows={3}
              />
            </div>

            {/* Campaign Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="campaign_type">Campaign Type</Label>
                <Select value={formData.campaign_type} onValueChange={(value) => setFormData({ ...formData, campaign_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bulk">Bulk</SelectItem>
                    <SelectItem value="adhoc">Ad-hoc</SelectItem>
                    <SelectItem value="trigger">Trigger</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="lookalike">Lookalike</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="trigger_type">Trigger Type</Label>
                <Input
                  id="trigger_type"
                  value={formData.trigger_type}
                  onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
                  placeholder="e.g., manual, automated"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="e.g., 1.0, 2.1"
              />
            </div>

            {/* Channels */}
            <div className="space-y-2">
              <Label>Channels</Label>
              <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-2 border rounded-md">
                {channels.map((channel) => (
                  <Badge key={channel} variant="secondary" className="flex items-center gap-1">
                    {channel}
                    <button
                      type="button"
                      onClick={() => removeChannel(channel)}
                      className="hover:bg-destructive/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {channels.length === 0 && (
                  <span className="text-muted-foreground text-sm">No channels added</span>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newChannel}
                  onChange={(e) => setNewChannel(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, addChannel)}
                  placeholder="Add channel (e.g., email, linkedin)"
                />
                <Button type="button" onClick={addChannel} size="icon" variant="outline">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Target Regions section removed (Phase I / Q7 2026-04-22).
                Regions are now a variant dimension in the Campaign-level
                Dimensions panel above and feed auto-variant generation.
                The legacy `campaigns.target_regions` TEXT[] is kept in
                the DB as deprecated metadata and is no longer edited here. */}

            {/* Links */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="clay_table_link">Clay Table Link</Label>
                <Input
                  id="clay_table_link"
                  type="url"
                  value={formData.clay_table_link}
                  onChange={(e) => setFormData({ ...formData, clay_table_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="clickup_task_link">ClickUp Task Link</Label>
                <Input
                  id="clickup_task_link"
                  type="url"
                  value={formData.clickup_task_link}
                  onChange={(e) => setFormData({ ...formData, clickup_task_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="copywriting_doc_link">Copywriting Doc Link</Label>
                <Input
                  id="copywriting_doc_link"
                  type="url"
                  value={formData.copywriting_doc_link}
                  onChange={(e) => setFormData({ ...formData, copywriting_doc_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}